import React, { useEffect, useState, useCallback } from "react";
import { Spinner, Card, Table, Badge, Button, Modal, Form, Row, Col, Alert as BsAlert } from "react-bootstrap";
import SafetyAgenzieService from "../../services/safety/SafetyAgenzieService";

/**
 * SafetyAgenziePage
 *
 * Pannello AMMINISTRATIVO del modulo "Gestione Adempimenti Sicurezza".
 * Riservato ad Admin / Superpartner.
 *
 * Le agenzie NON si creano qui: provengono dalla tabella `agenzie` (lato
 * corsi). Qui l'Admin:
 *  - attiva/disattiva manualmente il GAS per ogni agenzia (safety_enabled)
 *  - vede i conteggi di compliance (lavoratori, sedi, aziende)
 *  - configura le soglie di notifica scadenze per agenzia
 */
export default function SafetyAgenziePage() {
    const [loading, setLoading] = useState(true);
    const [agenzie, setAgenzie] = useState([]);
    const [errore, setErrore] = useState(null);
    const [savingId, setSavingId] = useState(null);

    const [showSettings, setShowSettings] = useState(false);
    const [settingsForm, setSettingsForm] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setErrore(null);
        try {
            const resp = await SafetyAgenzieService.getAll();
            const data = resp && typeof resp.json === "function" ? await resp.json() : resp;
            setAgenzie(Array.isArray(data) ? data : []);
        } catch (err) {
            setErrore(err?.message || "Errore nel caricamento delle agenzie");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const handleToggle = async (ag) => {
        const nuovoStato = ag.safety_enabled === 1 ? 0 : 1;
        if (nuovoStato === 0 && !window.confirm(`Disattivare il modulo Sicurezza per "${ag.denominazione}"? I dati restano salvati ma l'agenzia non vedrà più la sezione.`)) {
            return;
        }
        setSavingId(ag.id_agenzia);
        setErrore(null);
        try {
            await SafetyAgenzieService.toggleGas(ag.id_agenzia, nuovoStato);
            await load();
        } catch (err) {
            setErrore(err?.message || "Errore nell'aggiornamento dello stato GAS");
        } finally {
            setSavingId(null);
        }
    };

    const openSettings = (ag) => {
        setSettingsForm({
            id_agenzia: ag.id_agenzia,
            denominazione: ag.denominazione,
            soglia_giorni_alert_1: ag.soglia_giorni_alert_1 ?? 90,
            soglia_giorni_alert_2: ag.soglia_giorni_alert_2 ?? 60,
            soglia_giorni_alert_3: ag.soglia_giorni_alert_3 ?? 30,
            email_notifiche_to: ag.email_notifiche_to ?? "",
        });
        setShowSettings(true);
    };

    const saveSettings = async () => {
        if (!settingsForm) return;
        setSavingSettings(true);
        setErrore(null);
        try {
            await SafetyAgenzieService.updateSettings(settingsForm);
            setShowSettings(false);
            setSettingsForm(null);
            await load();
        } catch (err) {
            setErrore(err?.message || "Errore nel salvataggio delle impostazioni");
        } finally {
            setSavingSettings(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
                <Spinner animation="border" />
            </div>
        );
    }

    const attive = agenzie.filter(a => a.safety_enabled === 1).length;

    return (
        <div className="p-2 p-md-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <div>
                    <h4 className="mb-1">
                        <i className="bi bi-buildings me-2 text-success" />
                        Agenzie — Gestione Adempimenti Sicurezza
                    </h4>
                    <div className="text-muted small">
                        Attivazione del modulo Sicurezza per agenzia. Le agenzie provengono dall&apos;anagrafica corsi.
                        Anche l&apos;ente HE (eduform.app) e&apos; un&apos;agenzia: attiva il GAS e usa il selettore
                        contesto nelle pagine operative per gestire la propria compliance interna.
                    </div>
                </div>
                <Badge bg="success" className="fs-6">{attive} attive su {agenzie.length}</Badge>
            </div>

            {errore && <BsAlert variant="danger" onClose={() => setErrore(null)} dismissible>{errore}</BsAlert>}

            <Card className="border-0 shadow-sm">
                <Card.Body>
                    {agenzie.length === 0 ? (
                        <div className="text-muted py-4 text-center">Nessuna agenzia disponibile.</div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Agenzia</th>
                                        <th className="text-center">Stato GAS</th>
                                        <th className="text-end">Lavoratori</th>
                                        <th className="text-end">Sedi</th>
                                        <th className="text-end">Aziende</th>
                                        <th className="text-end">Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {agenzie.map(ag => {
                                        const attiva = ag.safety_enabled === 1;
                                        const busy = savingId === ag.id_agenzia;
                                        return (
                                            <tr key={ag.id_agenzia}>
                                                <td>
                                                    <div className="fw-semibold">{ag.denominazione}</div>
                                                    <div className="text-muted small">ID {ag.id_agenzia}</div>
                                                </td>
                                                <td className="text-center">
                                                    {attiva
                                                        ? <Badge bg="success">Attivo</Badge>
                                                        : <Badge bg="secondary">Disattivo</Badge>}
                                                </td>
                                                <td className="text-end">
                                                    {ag.num_lavoratori_attivi ?? 0}
                                                    <span className="text-muted small"> / {ag.num_lavoratori ?? 0}</span>
                                                </td>
                                                <td className="text-end">{ag.num_sedi ?? 0}</td>
                                                <td className="text-end">{ag.num_aziende ?? 0}</td>
                                                <td className="text-end">
                                                    <div className="d-inline-flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant={attiva ? "outline-secondary" : "success"}
                                                            disabled={busy}
                                                            onClick={() => handleToggle(ag)}
                                                        >
                                                            {busy
                                                                ? <Spinner animation="border" size="sm" />
                                                                : attiva ? "Disattiva" : "Attiva GAS"}
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            disabled={!attiva}
                                                            title={attiva ? "Impostazioni notifiche" : "Attiva prima il GAS"}
                                                            onClick={() => openSettings(ag)}
                                                        >
                                                            <i className="bi bi-gear" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            <Modal show={showSettings} onHide={() => setShowSettings(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-5">
                        Impostazioni notifiche — {settingsForm?.denominazione}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small">
                        Soglie (in giorni) di preavviso per le scadenze di attestati, visite mediche e documenti.
                    </p>
                    <Row className="g-3">
                        <Col xs={4}>
                            <Form.Label>1° alert</Form.Label>
                            <Form.Control
                                type="number" min={1}
                                value={settingsForm?.soglia_giorni_alert_1 ?? 90}
                                onChange={(e) => setSettingsForm(f => ({ ...f, soglia_giorni_alert_1: e.target.value }))}
                            />
                        </Col>
                        <Col xs={4}>
                            <Form.Label>2° alert</Form.Label>
                            <Form.Control
                                type="number" min={1}
                                value={settingsForm?.soglia_giorni_alert_2 ?? 60}
                                onChange={(e) => setSettingsForm(f => ({ ...f, soglia_giorni_alert_2: e.target.value }))}
                            />
                        </Col>
                        <Col xs={4}>
                            <Form.Label>3° alert</Form.Label>
                            <Form.Control
                                type="number" min={1}
                                value={settingsForm?.soglia_giorni_alert_3 ?? 30}
                                onChange={(e) => setSettingsForm(f => ({ ...f, soglia_giorni_alert_3: e.target.value }))}
                            />
                        </Col>
                        <Col xs={12}>
                            <Form.Label>Email per le notifiche</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="notifiche@agenzia.it"
                                value={settingsForm?.email_notifiche_to ?? ""}
                                onChange={(e) => setSettingsForm(f => ({ ...f, email_notifiche_to: e.target.value }))}
                            />
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={() => setShowSettings(false)}>Annulla</Button>
                    <Button variant="primary" onClick={saveSettings} disabled={savingSettings}>
                        {savingSettings ? <Spinner animation="border" size="sm" /> : "Salva"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
